import { Download, Smartphone, Share, SquarePlus, Info } from 'lucide-react';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerFooter,
  DrawerTitle,
  DrawerDescription,
} from '@components/ui/drawer';
import { Button } from '@components/ui/button';
import type { UseInstallPromptReturn } from '@hooks/useInstallPrompt';
import type { DesktopBrowser } from '@lib/pwa/install-utils';
import { useAmplitudeTracking } from '@hooks/useAmplitudeTracking';
import { ANALYTICS_EVENTS } from '@lib/analytics-events';

// ── Types ──────────────────────────────────────────────────────────────────

export interface InstallPromptDrawerProps {
  prompt: UseInstallPromptReturn;
  desktopBrowser?: DesktopBrowser;
}

// ── Android content ────────────────────────────────────────────────────────

function AndroidContent({ prompt }: { prompt: UseInstallPromptReturn }) {
  const { trackEvent } = useAmplitudeTracking();

  return (
    <>
      <DrawerHeader className="items-center gap-4 px-6 pt-6 pb-2 text-center sm:text-center">
        <img src="/assets/homescreen_icon.svg" className="mx-auto h-16 w-16" alt="" />
        <DrawerTitle className="text-xl font-bold">Keep Meetball close</DrawerTitle>
        <DrawerDescription className="mx-auto max-w-md space-y-2 text-base text-foreground/70">
          <span className="block">The right person could show up any time.</span>
          <span className="block">Add Meetball to your home screen so you're ready.</span>
        </DrawerDescription>
      </DrawerHeader>

      <div className="px-6 py-2">
        <div className="flex items-start gap-3 rounded-xl bg-muted p-3">
          <Smartphone className="mt-0.5 h-5 w-5 shrink-0 text-emerald-500" />
          <p className="text-sm text-muted-foreground">
            No app store. No big download. It takes a second.
          </p>
        </div>
      </div>

      <DrawerFooter className="gap-3 px-6 pb-safe">
        <Button
          type="button"
          onClick={prompt.handleInstall}
          disabled={prompt.isInstalling}
          className="h-12 w-full rounded-full bg-mb-teal font-semibold text-mb-ink shadow-glow-teal hover:bg-mb-teal"
        >
          {prompt.isInstalling ? 'Adding…' : 'Add to Home Screen'}
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => {
            trackEvent(ANALYTICS_EVENTS.cta_clicked, {
              cta_name: 'install_prompt_not_now',
              source_context: `install_prompt_${prompt.platform}`,
            });
            prompt.handleDismiss();
          }}
          className="h-12 w-full rounded-full"
        >
          Not Now
        </Button>
      </DrawerFooter>
    </>
  );
}

// ── iOS content ────────────────────────────────────────────────────────────

function IosContent({ prompt }: { prompt: UseInstallPromptReturn }) {
  const { trackEvent } = useAmplitudeTracking();

  return (
    <>
      <DrawerHeader className="items-center gap-4 px-6 pt-6 pb-2 text-center sm:text-center">
        <img src="/assets/homescreen_icon.svg" className="mx-auto h-16 w-16" alt="" />
        <DrawerTitle className="text-xl font-bold">Keep Meetball close</DrawerTitle>
        <DrawerDescription className="mx-auto max-w-md space-y-2 text-base text-foreground/70">
          <span className="block">Someone nearby might need exactly what you know.</span>
          <span className="block">No app store. No download. Just two taps.</span>
        </DrawerDescription>
      </DrawerHeader>

      <div className="px-6 py-2">
        <div className="rounded-xl bg-muted p-4">
          <ol className="space-y-4">
            <li className="flex items-center gap-3">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-mb-teal-light text-xs font-bold text-black">
                1
              </span>
              <span className="text-sm">
                Tap the{' '}
                <span className="inline-flex items-center gap-1 rounded-full bg-background px-2 py-0.5">
                  <Share className="h-3.5 w-3.5" />
                </span>{' '}
                <strong>Share</strong> button
              </span>
            </li>
            <li className="flex items-center gap-3">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-mb-teal-light text-xs font-bold text-black">
                2
              </span>
              <span className="text-sm">
                Tap{' '}
                <span className="inline-flex items-center gap-1 rounded-full bg-background px-2 py-0.5">
                  <SquarePlus className="h-3.5 w-3.5" />
                </span>{' '}
                <strong>Add to Home Screen</strong>
              </span>
            </li>
          </ol>
        </div>
      </div>

      <DrawerFooter className="gap-3 px-6 pb-safe">
        <Button
          type="button"
          onClick={prompt.handleDismiss}
          className="h-12 w-full rounded-full bg-mb-teal font-semibold text-mb-ink shadow-glow-teal hover:bg-mb-teal"
        >
          Got it
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => {
            trackEvent(ANALYTICS_EVENTS.cta_clicked, {
              cta_name: 'install_prompt_not_now',
              source_context: `install_prompt_${prompt.platform}`,
            });
            prompt.handleDismiss();
          }}
          className="h-12 w-full rounded-full"
        >
          Not Now
        </Button>
      </DrawerFooter>
    </>
  );
}

// ── Desktop content ────────────────────────────────────────────────────────

function DesktopContent({
  prompt,
  browser,
}: {
  prompt: UseInstallPromptReturn;
  browser: DesktopBrowser;
}) {
  const { trackEvent } = useAmplitudeTracking();

  return (
    <>
      <DrawerHeader className="items-center gap-4 px-6 pt-6 pb-2 text-center sm:text-center">
        <img src="/assets/homescreen_icon.svg" className="mx-auto h-16 w-16" alt="" />
        <DrawerTitle className="text-xl font-bold">Keep Meetball one click away</DrawerTitle>
        <DrawerDescription className="mx-auto max-w-md space-y-2 text-base text-foreground/70">
          <span className="block">The right person is closer than you think.</span>
          <span className="block">Add Meetball as an app. No download.</span>
        </DrawerDescription>
      </DrawerHeader>

      <div className="px-6 py-2">
        <div className="flex items-start gap-3 rounded-xl bg-muted p-4">
          <Info className="mt-0.5 h-5 w-5 shrink-0 text-mb-yellow" />
          <p className="text-sm text-muted-foreground">
            {browser === 'safari' ? (
              <>
                Go to <strong>File → Add to Dock</strong>.
              </>
            ) : (
              <>
                Look for the <strong>install icon</strong>{' '}
                <span className="inline-flex items-center gap-1 rounded-full bg-background px-2 py-0.5">
                  <Download className="h-3.5 w-3.5" />
                </span>{' '}
                in your address bar
              </>
            )}
          </p>
        </div>
      </div>

      <DrawerFooter className="gap-3 px-6 pb-safe">
        <Button
          type="button"
          onClick={prompt.handleDismiss}
          className="h-12 w-full rounded-full bg-mb-teal font-semibold text-mb-ink shadow-glow-teal hover:bg-mb-teal"
        >
          Got it
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => {
            trackEvent(ANALYTICS_EVENTS.cta_clicked, {
              cta_name: 'install_prompt_not_now',
              source_context: `install_prompt_${prompt.platform}`,
            });
            prompt.handleDismiss();
          }}
          className="h-12 w-full rounded-full"
        >
          Not Now
        </Button>
      </DrawerFooter>
    </>
  );
}

// ── Main component ─────────────────────────────────────────────────────────

export function InstallPromptDrawer({
  prompt,
  desktopBrowser = 'chromium',
}: InstallPromptDrawerProps) {
  const renderContent = () => {
    if (
      prompt.hasDeferredPrompt &&
      (prompt.platform === 'android' || prompt.platform === 'desktop')
    ) {
      return <AndroidContent prompt={prompt} />;
    }
    if (prompt.platform === 'ios') {
      return <IosContent prompt={prompt} />;
    }
    return <DesktopContent prompt={prompt} browser={desktopBrowser} />;
  };

  return (
    // eslint-disable-next-line @typescript-eslint/no-empty-function -- intentional no-op: overlay click must not dismiss
    <Drawer open={prompt.isOpen} onOpenChange={() => {}} dismissible={false}>
      <DrawerContent hideHandle>{renderContent()}</DrawerContent>
    </Drawer>
  );
}
